/**
 * How an admin-defined `/` kind's STYLE reaches the cells: one injected
 * stylesheet (F-17-type, 2026-09-04).
 *
 * Every run of a custom kind wears the class `tt-k-<id>` (ui/text-type.ts
 * `textTypeClassName`) and nothing else — the renderer does not look the kind
 * up. This module writes the rules those classes mean, so restyling a kind
 * is one stylesheet rewrite and zero re-renders of a 10k-row grid, and a run
 * whose kind is unknown here simply has no rule and paints in the cell's own
 * ink (still bracketed, so the reader can see it is a kind).
 *
 * The rules follow the split app.css makes for the built-ins: ink and the
 * painted semi-bold are METRIC-NEUTRAL and always on (safe over an editor's
 * transparent textarea); the italic hangs off `.tt-shown`, which only a
 * finished cell/block carries, because it changes the text's width
 * (ui/MarkedText.tsx explains the mirror-layer constraint).
 *
 * Dark mode (F-122): an admin picks ONE ink, against the light surface. The
 * same value on a dark surface is often unreadable (a navy that reads fine on
 * white vanishes on #1f232b), so a dark-theme counterpart is DERIVED — hue
 * kept, lightness lifted into a readable band — under the same two selectors
 * tokens.css uses. Nothing is stored; the kind still has one color.
 */
import type { TextKind } from "@kola/shared";

import { hslToRgb, parseHexColor, rgbaToHex, rgbToHsl } from "../../core/color.js";

/** The `<style>` element's id — one per document, rewritten in place. */
export const TEXT_KIND_STYLE_ID = "kola-text-kinds";

/** Class prefix a custom kind's runs wear; `${prefix}${id}`. */
export const TEXT_KIND_CLASS_PREFIX = "tt-";

/**
 * Lightness floor for ink on the dark surface. The built-in dark inks sit
 * around 0.6–0.8 (#4ade80 ≈ 0.64, #fca5a5 ≈ 0.82); below ~0.62 a saturated
 * hue starts to sink into the surface.
 */
export const DARK_INK_MIN_LIGHTNESS = 0.62;

/**
 * The same ink, made to read on a dark surface: hue and saturation kept,
 * lightness lifted to at least `DARK_INK_MIN_LIGHTNESS`. A color that is
 * already light enough comes back unchanged (an admin who picked a pastel
 * meant it). Null for anything that is not `#rrggbb` — the server refuses
 * those, so this is only ever a guard against a stale client.
 */
export function darkThemeInk(color: string): string | null {
  // `parseHexColor` also reads #rgb and #rrggbbaa; a kind's ink is #rrggbb only.
  if (safeHex(color) === null) return null;
  const rgb = parseHexColor(color);
  if (rgb === null) return null;
  const hsl = rgbToHsl(rgb);
  if (hsl.l >= DARK_INK_MIN_LIGHTNESS) return color.toLowerCase();
  return rgbaToHex(hslToRgb({ ...hsl, l: DARK_INK_MIN_LIGHTNESS }));
}

/** Only what the server would have accepted may reach a stylesheet. */
function safeHex(color: string): string | null {
  return /^#[0-9a-fA-F]{6}$/.test(color) ? color.toLowerCase() : null;
}

/** Only an id the server minted can become a class name (`isTextKindId`'s alphabet). */
function safeId(id: string): string | null {
  return /^[A-Za-z0-9_-]{1,64}$/.test(id) ? id : null;
}

/**
 * The stylesheet text for `kinds`. PURE — the DOM write is `applyTextKindStyles`.
 *
 * Painted semi-bold uses the same text-shadow trick as `.tt-visual` in
 * app.css, for the same reason: a real `font-weight` would re-measure the
 * text and slide every glyph off the caret of the editors' transparent
 * textareas.
 */
export function textKindStylesheet(kinds: readonly TextKind[]): string {
  const light: string[] = [];
  const dark: string[] = [];
  for (const kind of kinds) {
    const id = safeId(kind.id);
    const color = safeHex(kind.color);
    if (id === null || color === null) continue;
    const cls = `.${TEXT_KIND_CLASS_PREFIX}${id}`;
    const decl = [`color: ${color};`];
    if (kind.bold) decl.push("text-shadow: 0.25px 0 0 currentColor, -0.25px 0 0 currentColor;");
    light.push(`${cls} { ${decl.join(" ")} }`);
    if (kind.italic) light.push(`.tt-shown ${cls} { font-style: italic; }`);
    const darkInk = darkThemeInk(color);
    if (darkInk !== null && darkInk !== color) dark.push(`${cls} { color: ${darkInk}; }`);
  }
  const out = [...light];
  if (dark.length > 0) {
    out.push(
      "@media (prefers-color-scheme: dark) {",
      ...dark.map((rule) => `  :root:not([data-theme="light"]) ${rule}`),
      "}",
      ...dark.map((rule) => `:root[data-theme="dark"] ${rule}`),
    );
  }
  return out.join("\n");
}

/**
 * Write (or rewrite) the document's kind stylesheet. Safe to call before the
 * DOM exists (tests, SSR-ish environments): it simply does nothing.
 */
export function applyTextKindStyles(kinds: readonly TextKind[]): void {
  if (typeof document === "undefined") return;
  let style = document.getElementById(TEXT_KIND_STYLE_ID);
  if (style === null) {
    style = document.createElement("style");
    style.id = TEXT_KIND_STYLE_ID;
    document.head.appendChild(style);
  }
  style.textContent = textKindStylesheet(kinds);
}
